import { MixedSchema } from '@principle-theorem/editor';
import {
  type DocumentReference,
  TypeGuard,
  isDocRef,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { IDocumentSettings, Orientation, PaperSize } from './document-settings';
import { type IOrganisation } from '../organisation/organisation';
import { type IPatient } from '../patient/patient';
import { ITemplateDefinition } from '../templating/template-definition';
import { SystemTemplate } from '../templating/templating';

export interface ITemplateDefinitionWithName {
  templateDoc: ITemplateDefinition;
  template: SystemTemplate;
}

export interface IResolvedTemplateWithName {
  content: MixedSchema;
  template: SystemTemplate;
}

export interface IPDFContent {
  body: MixedSchema;
  header?: MixedSchema;
  footer?: MixedSchema;
  documentSettings?: IDocumentSettings;
}

export interface IPDFContentRequest {
  organisationRef: DocumentReference<IOrganisation>;
  bodyHtml: string;
  headerHtml?: string;
  footerHtml?: string;
  documentSettings?: IDocumentSettings;
  patientRef?: DocumentReference<IPatient>;
  fileName?: string;
}

export interface IPDFContentResponse {
  storageUrl: string;
}

export interface ISavePdfToPatientFileRequest extends IPDFContentRequest {
  patientRef: DocumentReference<IPatient>;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isDocumentSettings = TypeGuard.nilOr(
  TypeGuard.interface<IDocumentSettings>({
    fontFamily: TypeGuard.nilOr(isString),
    fontSize: TypeGuard.nilOr(isString),
    lineHeight: TypeGuard.nilOr(isString),
    paragraphSpacing: TypeGuard.nilOr(isString),
    marginTop: TypeGuard.nilOr(isFiniteNumber),
    marginBottom: TypeGuard.nilOr(isFiniteNumber),
    marginLeft: TypeGuard.nilOr(isFiniteNumber),
    marginRight: TypeGuard.nilOr(isFiniteNumber),
    paperSize: TypeGuard.nilOr(TypeGuard.enumValue(PaperSize)),
    orientation: TypeGuard.nilOr(TypeGuard.enumValue(Orientation)),
  })
);

export const isGeneratePdfRequest = TypeGuard.interface<IPDFContentRequest>({
  organisationRef: isDocRef,
  bodyHtml: isString,
  headerHtml: TypeGuard.nilOr(isString),
  footerHtml: TypeGuard.nilOr(isString),
  documentSettings: isDocumentSettings,
  patientRef: TypeGuard.nilOr(isDocRef),
  fileName: TypeGuard.nilOr(isString),
});

export function isSavePdfToPatientFileRequest(
  request: IPDFContentRequest
): request is ISavePdfToPatientFileRequest {
  return !!request.patientRef;
}
